import { type FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Trans } from "react-i18next";

import { emailMustBeValid, emailMustHaveAtSign } from "../../helpers/password-errors";

import { PasswordForm, PasswordFormValue } from "components/PasswordForm";
import { t } from "i18n/i18n";
import { Checkbox } from "mds/components/Checkbox";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { LoadingDotsIcon } from "mds/icons";
import { Link } from "utils/routing";

export type AccountFormValue = PasswordFormValue & {
  email: string;
  first_name: string;
  last_name: string;
  eula: boolean;
  send_onboarding_email?: boolean;
};

interface AccountFormProps {
  onCancel?: () => void;
  onSubmit: (data: AccountFormValue) => Promise<void>;
  onShowLogin?: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<AccountFormValue>;
  forAdding?: boolean;
}

export const AccountForm: FC<AccountFormProps> = ({
  onSubmit,
  onShowLogin,
  onCancel,
  isLoading = false,
  defaultValues,
  forAdding = false,
}) => {
  const form = useForm<AccountFormValue>({ defaultValues });

  const handleSubmit: SubmitHandler<AccountFormValue> = (formData) => onSubmit(formData);

  const isSubmitting = form.formState.isSubmitting || isLoading;

  return (
    <>
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(handleSubmit)}>
          <div className="mb-3 flex w-full flex-col gap-3">
            <FormField
              id="email"
              label={forAdding ? t("user_info.user_email") : t("user_info.email")}
              name="email"
              required
            >
              {(controlProps) => (
                <TextField
                  {...form.register("email", {
                    required: t("error.field_required"),
                    validate: {
                      emailMustHaveAtSign,
                      emailMustBeValid,
                    },
                  })}
                  {...controlProps}
                  autoComplete="email"
                  disabled={!!defaultValues}
                  eventName="Account - Sign up form / Email"
                  eventPropertyKey="userEmail"
                  type="email"
                  required
                />
              )}
            </FormField>

            <FormField id="first_name" label={t("user_info.first_name")} name="first_name" required>
              {(controlProps) => (
                <TextField
                  {...form.register("first_name", {
                    required: t("error.field_required"),
                  })}
                  {...controlProps}
                  autoComplete="given-name"
                  eventName="Account - Sign up form / Given name"
                  eventPropertyKey="userGivenName"
                  type="text"
                  required
                />
              )}
            </FormField>

            <FormField id="last_name" label={t("user_info.last_name")} name="last_name">
              {(controlProps) => (
                <TextField
                  {...form.register("last_name")}
                  {...controlProps}
                  autoComplete="family-name"
                  eventName="Account - Sign up form / Family name"
                  eventPropertyKey="userFamilyName"
                  type="text"
                />
              )}
            </FormField>
          </div>

          {forAdding ? (
            <>
              <div className="mb-3">
                <Checkbox
                  {...form.register("send_onboarding_email")}
                  errorMessage={form.formState.errors.send_onboarding_email?.message}
                  eventName="Account - Add user form / Send onboarding email"
                  id="send_onboarding_email"
                >
                  {t("admin.add_user.send_invitation_email")}
                </Checkbox>
              </div>

              <div className="mt-8 flex items-center justify-end gap-2">
                <SquareButton
                  disabled={isSubmitting}
                  eventName="Account - Add user / Cancel"
                  kind="tertiary"
                  size="m"
                  onClick={onCancel}
                >
                  {t("common.cancel")}
                </SquareButton>

                <SquareButton eventName="Account - Add user" kind="accent" size="m" type="submit">
                  {t("admin.add_user.add_user")}
                </SquareButton>
                {isSubmitting && <LoadingDotsIcon aria-label={t("common.submitting")} />}
              </div>
            </>
          ) : (
            <>
              <div className="mb-3 flex w-full flex-col gap-3">
                <PasswordForm eventPrefix="Account - Sign up form / " isNewPassword />
              </div>

              <div>
                <Checkbox
                  {...form.register("eula", {
                    required: t("user_options_menu.redeem.eula_required"),
                  })}
                  errorMessage={form.formState.errors.eula?.message}
                  eventName="Account - Sign up form / Terms Agreed"
                  id="eula"
                  required
                >
                  <div>
                    <Trans i18nKey="user_info.eula">
                      I agree to the
                      <Link
                        className="text-blue-shade-20"
                        target="_blank"
                        to="/terms-of-service-en"
                      >
                        Terms of Service
                      </Link>
                      and
                      <Link className="text-blue-shade-20" target="_blank" to="/privacy-policy-en">
                        Privacy Policy
                      </Link>
                    </Trans>
                  </div>
                </Checkbox>
              </div>

              <div className="mt-4 flex items-center gap-2">
                <SquareButton
                  eventName="Account - Confirm sign up"
                  kind="accent"
                  size="m"
                  type="submit"
                >
                  {defaultValues ? t("user_info.set_up_account") : t("user_info.sign_up")}
                </SquareButton>
                {isSubmitting && <LoadingDotsIcon aria-label={t("common.submitting")} />}
              </div>
            </>
          )}
        </form>
      </FormProvider>

      {onShowLogin && (
        <div className="mt-8 text-black-tint-20">
          {t("user_options_menu.redeem.already_have_account")}

          <SquareButton
            className="mt-4"
            eventName="Account - Sign in"
            kind="tertiary"
            size="m"
            onClick={onShowLogin}
          >
            {t("user_info.sign_in")}
          </SquareButton>
        </div>
      )}
    </>
  );
};
